import React, { useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import * as Yup from 'yup';
import Menu from '../../components/Menu';

import Input from '../../components/Input';
import ImageInput from '../../components/ImageInput'; // Usado para avatar
import FileInput from '../../components/FileInput'; // Novo componente para upload de PDF
import Textarea from '../../components/Textarea';
import Button from '../../components/Button';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';
import { Container, Content, ContentContainer } from './styles';

import getValidationErrors from '../../utils/getValidationErrors';

// Interface para os dados do formulário de artigo
interface ArticleFormData {
  avatar: File; // Para a imagem de capa (avatar_url no schema)
  pdf: File; // O arquivo PDF (pdf_url no schema)
  title: string;
  authors: string;
  abstract: string;
}

const CriarArtigos: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (data: ArticleFormData) => {
    try {
      setIsLoading(true);
      formRef.current?.setErrors({});

      // Esquema de validação usando Yup
      const schema = Yup.object().shape({
        title: Yup.string().required('Título do artigo é obrigatório'),
        authors: Yup.string().required('Autores são obrigatórios'),
        abstract: Yup.string().required('O resumo é obrigatório'),
        // Não validamos o 'pdf' aqui com Yup porque ele é um File e não uma string URL
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      // Validação manual para os arquivos (avatar e pdf)
      if (!data.avatar) {
        addToast({
          type: 'info',
          title: 'Informação',
          description: 'Você precisa selecionar uma imagem de capa para o artigo',
        });
        setIsLoading(false);
        return;
      }

      if (!data.pdf) {
        addToast({
          type: 'info',
          title: 'Informação',
          description: 'Você precisa selecionar o arquivo PDF do artigo',
        });
        setIsLoading(false);
        return;
      }

      const formDataAvatar = new FormData();
      formDataAvatar.append('avatar', data.avatar);

      const formDataPdf = new FormData();
      formDataPdf.append('pdf', data.pdf);

      let avatarUrl: string | null = null;
      let pdfUrl: string | null = null;

      // Envia a imagem para o endpoint de arquivos
      const imageUploadResponse = await api.post('/files', formDataAvatar);
      if (imageUploadResponse.data && imageUploadResponse.data.path) {
        avatarUrl = imageUploadResponse.data.path;
      } else {
        addToast({ type: 'error', title: 'Erro no Upload', description: 'Falha ao fazer upload da imagem de capa.' });
        setIsLoading(false);
        return;
      }

      // Envia o PDF para o endpoint de arquivos
      const pdfUploadResponse = await api.post('/files', formDataPdf); // Supondo o mesmo endpoint /files ou um específico para PDFs
      if (pdfUploadResponse.data && pdfUploadResponse.data.path) {
        pdfUrl = pdfUploadResponse.data.path;
      } else {
        addToast({ type: 'error', title: 'Erro no Upload', description: 'Falha ao fazer upload do arquivo PDF.' });
        setIsLoading(false);
        return;
      }

      // Prepara os dados do artigo para envio, incluindo as URLs dos arquivos
      const articleData = {
        title: data.title,
        authors: data.authors,
        abstract: data.abstract,
        avatar_url: avatarUrl,
        pdf_url: pdfUrl, // Agora é a URL retornada do upload do PDF
      };

      // Envia os dados do artigo para o backend
      await api.post('/articles', articleData);

      addToast({
        type: 'success',
        title: 'Artigo Criado',
        description: 'O artigo foi criado com sucesso!',
      });

      setIsLoading(false);
      history.push('/artigos');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
        setIsLoading(false);
        return;
      }

      addToast({
        type: 'error',
        title: 'Erro na Criação do Artigo',
        description: 'Ocorreu um erro ao criar o artigo. Por favor, cheque os dados e tente novamente.',
      });
      setIsLoading(false);
    }
  }, [addToast, history]);

  return (
    <Container>
      <Menu />
      <Content>
        <ContentContainer>
          <h1>Criar Artigo</h1>

          <Form ref={formRef} onSubmit={handleSubmit}>
            <ImageInput name="avatar" /> {/* Campo para upload da imagem de capa */}

            <Input name="title" placeholder="Digite o Título do Artigo" label="Título" />
            <Input name="authors" placeholder="Digite os autores (separados por vírgula)" label="Autores" />
            <Textarea name="abstract" label="Resumo" placeholder="Digite o resumo do artigo" />
            <FileInput name="pdf" label="Arquivo PDF" /> {/* Novo campo para upload do PDF */}

            <Button type="submit" color="#2A2A29" disabled={isLoading}>
              {isLoading ? 'Carregando...' : 'Criar Artigo'}
            </Button>
          </Form>
        </ContentContainer>
      </Content>
    </Container>
  );
};

export default CriarArtigos;
